import { SignerAttachmentItem } from "./ParticipantScreen/components/AttachmentPolicy";
import strings from "global/constants/StringConstants";
import { FileField, RequestFile } from "models/interfaces";

const held = new Map<string, SignerAttachmentItem[]>();

let sequence = 0;

/** Ids only have to be unique within this page; the real ids arrive when the files are uploaded. */
export const makeLocalAttachmentItem = (file: File): SignerAttachmentItem => {
  sequence += 1;
  return {
    id: `local-${Date.now()}-${sequence}`,
    file,
    name: file.name,
    size: file.size,
    addedAt: new Date().toISOString(),
  };
};

export const getHeldAttachments = (fieldName: string): SignerAttachmentItem[] =>
  held.get(fieldName) ?? [];

export const setHeldAttachments = (
  fieldName: string,
  items: SignerAttachmentItem[],
) => {
  if (items.length) held.set(fieldName, items);
  else held.delete(fieldName);
};

export const heldAttachmentsElsewhere = (fieldName: string): number => {
  let total = 0;
  held.forEach((items, key) => {
    if (key !== fieldName) total += items.length;
  });
  return total;
};

/** Every held file with the field it belongs to, in the order the uploads should be attempted. */
export const allHeldAttachments = (): {
  fieldName: string;
  items: SignerAttachmentItem[];
}[] =>
  Array.from(held.entries()).map(([fieldName, items]) => ({
    fieldName,
    items,
  }));

/**
 * Every attachment field name on a request, across ALL of its files.
 *
 * The per-signer limit and the stored files are envelope-wide, so both callers of
 * {@link pruneHeldAttachments} have to look at the whole request rather than the selected file.
 */
export const attachmentFieldNames = (files?: RequestFile[]): string[] =>
  (files ?? []).flatMap((file) =>
    (file?.fields ?? [])
      .filter((field: FileField) => field?.type === strings.attachment)
      .map((field: FileField) => field?.name),
  );

/**
 * Drops anything held for a field that is no longer on the document.
 *
 * A file is held under its field's name, and nothing tells the store when the sender deletes that
 * field. Two things then go wrong, both before anything reaches the server: the send uploads against a
 * field the envelope no longer has and is refused with "No attachment was requested from you on this
 * document", leaving the envelope it just created stranded in Pending; and until then those files keep
 * counting towards the per-signer limit, so a fresh field can open with no slots left.
 *
 * Called with the names of every attachment field on the WHOLE request, never one file's worth --
 * pruning against a single file would throw away what is held for the other files' fields.
 */
export const pruneHeldAttachments = (liveFieldNames: string[]) => {
  const live = new Set(liveFieldNames.filter(Boolean));
  Array.from(held.keys()).forEach((fieldName) => {
    if (!live.has(fieldName)) held.delete(fieldName);
  });
};

export const hasHeldAttachments = (): boolean => held.size > 0;

/** Called once the envelope is sent, and whenever a self-sign draft is abandoned. */
export const clearHeldAttachments = () => held.clear();

export const ensureAttachmentFieldName = (existing?: string): string => {
  if (existing) return existing;
  sequence += 1;
  return `attachment-${Date.now()}-${sequence}`;
};
